import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  FileText,
  PackageCheck,
  Paperclip,
  RefreshCw,
  Truck,
} from 'lucide-react';
import { calculateVPELines } from '@growthdept/blocks/gift-pricing';
import {
  fetchPackagesByProject,
  fetchProject,
  fetchPurchaseOrders,
  generatePurchaseOrdersForPackage,
  getPurchaseDocSignedUrl,
  purchaseOrderConflicts,
  recordDelivery,
  updatePurchaseOrder,
  updatePurchaseOrderStatus,
  uploadPurchaseDoc,
} from '@/lib/api';
import { formatEur } from '@/lib/pdf-brand';
import type { SupplierConflict } from '@/lib/supplier-conflicts';
import type { GiftPackage, Project, PurchaseOrder } from '@/lib/types';

type StatusKey = PurchaseOrder['status'];

const STATUS_FLOW: { key: StatusKey; label: string; icon: typeof Circle }[] = [
  { key: 'draft', label: 'Concept', icon: Circle },
  { key: 'ordered', label: 'Besteld', icon: PackageCheck },
  { key: 'confirmed', label: 'Bevestigd', icon: CheckCircle2 },
  { key: 'delivered', label: 'Geleverd', icon: Truck },
];

export function PurchasePage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [packages, setPackages] = useState<GiftPackage[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [activePkgId, setActivePkgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activePkg = useMemo(
    () => packages.find((p) => p.id === activePkgId) ?? null,
    [packages, activePkgId]
  );

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [p, pkgs] = await Promise.all([fetchProject(id), fetchPackagesByProject(id)]);
      setProject(p);
      setPackages(pkgs);
      if (!activePkgId && pkgs[0]) setActivePkgId(pkgs[0].id);
      const targetPkgId = activePkgId ?? pkgs[0]?.id;
      if (targetPkgId) {
        setOrders(await fetchPurchaseOrders(targetPkgId));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [id, activePkgId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleGenerate() {
    if (!activePkg) return;
    setBusy(true);
    try {
      const vpeLines = calculateVPELines((activePkg.items ?? []) as never, activePkg.quantity);
      await generatePurchaseOrdersForPackage(activePkg.id, vpeLines as never);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Genereren mislukt');
    } finally {
      setBusy(false);
    }
  }

  async function handleStatus(po: PurchaseOrder, next: StatusKey) {
    // Bij overgang naar 'delivered': pakbon + aantal zijn vereist (data-entry).
    // Een AFWIJKING tussen besteld en geleverd blokkeert NIET (FR-6.3) — Mark mag een
    // afwijkend aantal vastleggen. Levering loopt via record_delivery: zet geleverd én
    // schrijft de restvoorraad weg (verwacht→beschikbaar).
    if (next === 'delivered') {
      const supplierName = po.supplier?.name ?? 'deze inkoop';
      if (!po.delivery_note_url) {
        setError(`Upload eerst de pakbon voor ${supplierName} voordat je op Geleverd klikt.`);
        return;
      }
      if (po.delivery_note_qty_reported == null) {
        setError(
          `Vul eerst het aantal eenheden van de pakbon (${supplierName}) in voordat je op Geleverd zet.`
        );
        return;
      }
      setError(null);
      await recordDelivery(po.id, po.delivery_note_qty_reported);
      await reload();
      return;
    }
    setError(null);
    await updatePurchaseOrderStatus(po.id, next);
    await reload();
  }

  async function handleRef(po: PurchaseOrder, ref: string) {
    await updatePurchaseOrderStatus(po.id, po.status, ref || null);
    await reload();
  }

  if (loading) return <div className="text-sm text-bmade-500">Laden...</div>;
  if (!project) return <div className="text-sm text-red-500">{error ?? 'Niet gevonden'}</div>;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to={`/projecten/${project.id}`}
            className="mb-1 inline-flex items-center gap-1 text-xs text-bmade-500 hover:text-bmade-800 dark:text-bmade-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-3 w-3" /> {project.name}
          </Link>
          <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Inkoop</h1>
          <p className="text-xs text-bmade-500 dark:text-bmade-400">
            Inkoopopdrachten per leverancier, status besteld → bevestigd → geleverd.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {packages.length > 1 && (
            <select
              value={activePkgId ?? ''}
              onChange={(e) => setActivePkgId(e.target.value)}
              className="rounded-md border border-bmade-200 bg-white px-2 py-1.5 text-xs text-bmade-950 dark:border-bmade-700 dark:bg-bmade-950 dark:text-white"
            >
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.variant_label ?? p.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={busy || !activePkg}
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
            {orders.length === 0 ? "Genereer PO's" : "Hergenereer concept-PO's"}
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="rounded-xl border border-bmade-200 bg-white p-6 text-sm text-bmade-500 dark:border-bmade-800 dark:bg-bmade-900/40">
          Nog geen inkoopopdrachten. Klik "Genereer PO's" om uit de pakket-inhoud per leverancier
          een conceptopdracht te maken. Concept-PO's worden bij volgende generatie overschreven,
          besteld/bevestigd/geleverd blijven staan.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((po) => (
            <PurchaseOrderCard
              key={po.id}
              po={po}
              conflicts={purchaseOrderConflicts(po, activePkg?.handling_date)}
              onStatus={(s) => handleStatus(po, s)}
              onRef={(r) => handleRef(po, r)}
              onChanged={reload}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PurchaseOrderCard({
  po,
  conflicts,
  onStatus,
  onRef,
  onChanged,
}: {
  po: PurchaseOrder;
  conflicts: SupplierConflict[];
  onStatus: (s: StatusKey) => void | Promise<void>;
  onRef: (r: string) => void | Promise<void>;
  onChanged: () => void | Promise<void>;
}) {
  const [ref, setRef] = useState(po.supplier_invoice_ref ?? '');
  useEffect(() => setRef(po.supplier_invoice_ref ?? ''), [po.supplier_invoice_ref]);

  const items = po.items ?? [];
  const total = items.reduce(
    (s, it) => s + (it.actual_invoice_price ?? it.quoted_unit_price) * it.total_units_ordered,
    0
  );

  const currentStep = STATUS_FLOW.findIndex((f) => f.key === po.status);
  const showPoUpload = po.status === 'confirmed' || po.status === 'delivered';
  // Pakbon-upload zichtbaar vanaf 'confirmed' zodat Mark eerst kan uploaden +
  // qty invullen voordat hij op 'Geleverd' klikt (anders catch-22).
  const showDeliveryUpload = po.status === 'confirmed' || po.status === 'delivered';
  // Som van bestelde units = wat Mark als referentie heeft voor pakbon-controle.
  const totalUnitsOrdered = items.reduce((s, it) => s + (it.total_units_ordered ?? 0), 0);

  return (
    <div className="rounded-xl border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40">
      <header className="flex items-center justify-between border-b border-bmade-200 px-4 py-3 dark:border-bmade-800">
        <div>
          <h3 className="text-base font-semibold text-bmade-950 dark:text-white">
            {po.supplier?.name ?? 'Onbekende leverancier'}
          </h3>
          <p className="text-xs text-bmade-500">
            {items.length} regels · totaal {formatEur(total)} excl btw
          </p>
        </div>
      </header>

      <div className="space-y-3 px-4 py-3">
        {conflicts.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              Let op: schuurt met de leveranciersvoorwaarden
            </div>
            <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-200">
              {conflicts.map((c, i) => (
                <li key={i}>• {c.message}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-1 items-center gap-2">
            {STATUS_FLOW.map(({ key, label, icon: Icon }, idx) => {
              const active = idx <= currentStep;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onStatus(key)}
                  className={`flex flex-1 items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? 'border-accent-400 bg-accent-500/10 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                      : 'border-bmade-200 bg-white text-bmade-600 hover:border-bmade-400 dark:border-bmade-700 dark:bg-bmade-950 dark:text-bmade-400'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-0.5 block text-[10px] uppercase tracking-wider text-bmade-500">
            Bevestigings-referentie (leverancier-bestelnummer)
          </label>
          <input
            type="text"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            onBlur={() => ref !== (po.supplier_invoice_ref ?? '') && onRef(ref)}
            placeholder="bv. 2026-OR-04821"
            className="w-full rounded border border-bmade-200 bg-white px-2 py-1 text-xs text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
          />
        </div>

        {showPoUpload && (
          <DocUploadRow
            label="Leverancier-PO bevestiging"
            hint="Upload de PDF of screenshot van de bevestigings-mail van de leverancier."
            existingPath={po.supplier_order_doc_url ?? null}
            onUpload={async (file) => {
              const path = await uploadPurchaseDoc(po.id, 'po', file);
              await updatePurchaseOrder(po.id, { supplier_order_doc_url: path });
              await onChanged();
            }}
          />
        )}

        {showDeliveryUpload && (
          <DeliveryNoteRow po={po} totalUnitsOrdered={totalUnitsOrdered} onChanged={onChanged} />
        )}

        {items.length > 0 && (
          <div className="overflow-hidden rounded border border-bmade-200 dark:border-bmade-800">
            <table className="w-full text-xs">
              <thead className="bg-bmade-50 text-left text-[10px] uppercase tracking-wider text-bmade-500 dark:bg-bmade-900/60">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Artikel</th>
                  <th className="px-2 py-1.5 text-right font-medium">Colli</th>
                  <th className="px-2 py-1.5 text-right font-medium">Te bestellen</th>
                  <th className="px-2 py-1.5 text-right font-medium">Prijs</th>
                  <th className="px-2 py-1.5 text-right font-medium">Subtotaal</th>
                  <th className="px-2 py-1.5 text-right font-medium">Rest</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bmade-200 dark:divide-bmade-800">
                {items.map((it) => (
                  <tr key={it.id} className="text-bmade-700 dark:text-bmade-300">
                    <td className="px-2 py-1.5">{it.gift_product?.name ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right">{it.vpe_units_ordered}</td>
                    <td className="px-2 py-1.5 text-right font-medium text-bmade-950 dark:text-white">
                      {it.total_units_ordered}
                    </td>
                    <td className="px-2 py-1.5 text-right">{formatEur(it.quoted_unit_price)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">
                      {formatEur(it.quoted_unit_price * it.total_units_ordered)}
                    </td>
                    <td
                      className={`px-2 py-1.5 text-right ${
                        it.leftover_units > 0
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-bmade-500'
                      }`}
                    >
                      {it.leftover_units}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DocUploadRow({
  label,
  hint,
  existingPath,
  onUpload,
}: {
  label: string;
  hint: string;
  existingPath: string | null;
  onUpload: (file: File) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      await onUpload(file);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload mislukt');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const filename = existingPath ? existingPath.split('/').pop() : null;

  return (
    <div className="rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-bmade-500">{label}</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-accent-400 px-2 py-0.5 text-[11px] font-medium text-accent-700 hover:bg-accent-50 disabled:opacity-50 dark:text-accent-300 dark:hover:bg-accent-500/10"
        >
          <Paperclip className="h-3 w-3" />
          {busy ? 'Uploaden...' : existingPath ? 'Vervangen' : '+ Uploaden'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </div>
      {existingPath ? (
        <DocLink path={existingPath} filename={filename ?? 'document'} />
      ) : (
        <p className="text-[11px] text-bmade-500">{hint}</p>
      )}
      {err && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{err}</p>}
    </div>
  );
}

function DocLink({ path, filename }: { path: string; filename: string }) {
  const [loading, setLoading] = useState(false);
  async function openDoc() {
    setLoading(true);
    try {
      const url = await getPurchaseDocSignedUrl(path, 600);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setLoading(false);
    }
  }
  return (
    <button
      type="button"
      onClick={openDoc}
      disabled={loading}
      className="inline-flex items-center gap-1 text-[11px] text-accent-700 hover:underline disabled:opacity-50 dark:text-accent-300"
    >
      <FileText className="h-3 w-3" />
      {loading ? 'Openen...' : filename}
    </button>
  );
}

function DeliveryNoteRow({
  po,
  totalUnitsOrdered,
  onChanged,
}: {
  po: PurchaseOrder;
  totalUnitsOrdered: number;
  onChanged: () => void | Promise<void>;
}) {
  const initialQty = po.delivery_note_qty_reported ?? '';
  const [qty, setQty] = useState<string>(String(initialQty));
  useEffect(() => {
    setQty(String(po.delivery_note_qty_reported ?? ''));
  }, [po.delivery_note_qty_reported]);

  async function handleQtyBlur() {
    const parsed = qty.trim() === '' ? null : parseInt(qty, 10);
    if (parsed != null && (Number.isNaN(parsed) || parsed < 0)) return;
    if (parsed === (po.delivery_note_qty_reported ?? null)) return;
    await updatePurchaseOrder(po.id, { delivery_note_qty_reported: parsed });
    await onChanged();
  }

  const reportedNum = qty.trim() === '' ? null : parseInt(qty, 10);
  const mismatch =
    reportedNum != null &&
    !Number.isNaN(reportedNum) &&
    totalUnitsOrdered > 0 &&
    reportedNum !== totalUnitsOrdered;

  return (
    <div className="space-y-2">
      <DocUploadRow
        label="Pakbon"
        hint="Upload de pakbon (PDF of foto). Daarna geef je het werkelijk geleverde aantal door."
        existingPath={po.delivery_note_url ?? null}
        onUpload={async (file) => {
          const path = await uploadPurchaseDoc(po.id, 'delivery', file);
          await updatePurchaseOrder(po.id, { delivery_note_url: path });
          await onChanged();
        }}
      />

      <div className="rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <div className="mb-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Aantal geleverd (volgens pakbon)
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onBlur={() => void handleQtyBlur()}
            placeholder={`besteld: ${totalUnitsOrdered}`}
            className="w-32 rounded border border-bmade-200 bg-white px-2 py-1 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
          />
          <span className="text-xs text-bmade-500">van {totalUnitsOrdered} besteld</span>
        </div>
        {mismatch && (
          <div className="mt-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              Pakbon-aantal ({reportedNum}) wijkt af van besteld aantal ({totalUnitsOrdered}). Check
              de leverancier-PO en pakbon voor je dit als afgehandeld markeert.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
